import { useEffect, useMemo, useState } from "react";
import {
  ConfirmDialog,
  ExtendedCard,
  FormAlert,
  TableKit,
} from "@NP-Webber/crm-kit";
import {
  shellApi,
  type FormDto,
  type FormSubmissionDto,
} from "../../api/shellApi";
import { toClientApiError } from "../../api/httpClient";
import {
  DeleteOutlinedIcon,
  EditOutlinedIcon,
} from "../../ui/appIcons";
import { FormBuilderDialog } from "./FormBuilderDialog";
import type { FormBuilderSavePayload } from "./formSchema";
import { readFormSchema, SOURCE_TYPE_OPTIONS } from "./formSchema";

export type FormEntityCardProps = {
  open: boolean;
  form: FormDto | null;
  onClose: () => void;
  onChanged?: () => void;
};

export function FormEntityCard({
  open,
  form,
  onClose,
  onChanged,
}: FormEntityCardProps) {
  const [active, setActive] = useState<FormDto | null>(form);
  const [submissions, setSubmissions] = useState<FormSubmissionDto[]>([]);
  const [subsLoading, setSubsLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !form) return;
    setActive(form);
    setError(null);
    setEditOpen(false);
    setDeleteOpen(false);
    setSubsLoading(true);
    void shellApi
      .listSubmissions(form.id)
      .then(setSubmissions)
      .catch(() => setSubmissions([]))
      .finally(() => setSubsLoading(false));
  }, [open, form]);

  const publicUrl = active
    ? `${window.location.origin}/public/forms/${active.publicToken}`
    : "";

  const schema = useMemo(
    () => readFormSchema(active?.schemaJson),
    [active?.schemaJson],
  );

  const sourceLabel =
    SOURCE_TYPE_OPTIONS.find((o) => o.value === schema.sourceType)?.label ||
    schema.sourceType ||
    "—";

  const fieldCount = schema.sections.reduce(
    (sum, s) => sum + (s.fields?.length ?? 0),
    0,
  );

  const detailsFields = useMemo(
    () => [
      { key: "name", label: "שם", value: active?.name ?? "" },
      {
        key: "isAvailable",
        label: "זמין",
        value: active?.isAvailable ? "כן" : "לא",
      },
      { key: "sourceType", label: "מקור", value: sourceLabel },
      {
        key: "fields",
        label: "שדות",
        value: String(fieldCount),
      },
      { key: "publicUrl", label: "קישור ציבורי", value: publicUrl || "—" },
      {
        key: "description",
        label: "תיאור",
        value: active?.description?.trim() || "—",
      },
    ],
    [active, publicUrl, sourceLabel, fieldCount],
  );

  const handleSave = async (payload: FormBuilderSavePayload) => {
    if (!active) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await shellApi.updateForm(active.id, payload);
      setActive(updated);
      setEditOpen(false);
      onChanged?.();
    } catch (err) {
      setError(toClientApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!active) return;
    setBusy(true);
    setError(null);
    try {
      await shellApi.deleteForm(active.id);
      setDeleteOpen(false);
      onChanged?.();
      onClose();
    } catch (err) {
      setError(toClientApiError(err).message);
      setDeleteOpen(false);
    } finally {
      setBusy(false);
    }
  };

  if (!open || !active) {
    return null;
  }

  const submissionRows = submissions.map((s) => ({
    id: s.id,
    createdAt: new Date(s.createdAt).toLocaleString("he-IL"),
    preview: JSON.stringify(s.dataJson).slice(0, 120),
  }));

  const submissionsTab = (
    <div className="page-section__table-surface">
      <TableKit
        columns={[
          {
            key: "createdAt",
            title: "תאריך",
            sortable: true,
            filterable: true,
            minWidth: 140,
          },
          {
            key: "preview",
            title: "תוכן",
            filterable: true,
            minWidth: 220,
          },
        ]}
        data={submissionRows as unknown as Array<Record<string, unknown>>}
        loading={subsLoading}
        clientSideMode
        emptyMessage="אין שליחות"
        showFilters
        showColumnPicker
      />
    </div>
  );

  return (
    <>
      <ExtendedCard
        key={active.id}
        open
        onClose={onClose}
        title={active.name}
        subtitle={active.isAvailable ? "זמין לציבור" : "לא זמין"}
        detailsFields={detailsFields}
        headerActions={[
          {
            icon: <EditOutlinedIcon />,
            tooltip: "עריכה",
            onClick: () => {
              setError(null);
              setEditOpen(true);
            },
          },
          {
            icon: <DeleteOutlinedIcon />,
            tooltip: "מחיקה",
            onClick: () => setDeleteOpen(true),
          },
        ]}
        detailsHeaderExtra={
          error && !editOpen ? <FormAlert severity="error">{error}</FormAlert> : null
        }
        feedSources={{ activities: [], tasks: [], notes: [] }}
        hideFeedTab
        showDashboard={false}
        defaultTab={0}
        extraTabs={[{ label: "שליחות", content: submissionsTab }]}
      />

      <FormBuilderDialog
        open={editOpen}
        value={active}
        saving={busy}
        error={error}
        onClose={() => {
          if (!busy) {
            setEditOpen(false);
            setError(null);
          }
        }}
        onSave={handleSave}
      />

      <ConfirmDialog
        open={deleteOpen}
        onClose={() => {
          if (!busy) setDeleteOpen(false);
        }}
        title="מחיקת טופס"
        message={`למחוק את הטופס "${active.name}"?`}
        confirmText="מחיקה"
        cancelText="ביטול"
        loading={busy}
        onConfirm={() => {
          void handleDelete();
        }}
        onCancel={() => setDeleteOpen(false)}
      />
    </>
  );
}
